/**
 * Fulfilment routes on the dictionary admin surface (product brief
 * docs/features/catalogue-third-party-fulfilment/01_product_brief.md §8,
 * journeys A–D), aliased over the generated OpenAPI schema.
 *
 * Journeys A and B are composed by `views_module` (the matrix's route rows,
 * the in-house cell, the supplier drill-down, the index's route coverage);
 * journeys C and D by `dictionary_module` (the overnight banner, an item's
 * mappings). Every business state here — a supplier cost's freshness, the
 * in-house cell's kind, a route's availability — is decided server-side and
 * arrives discriminated; the pages only render it.
 *
 * Vocabulary (Ben, 23–24 Sep): *fulfilment route*, *dictionary*, and one
 * noun *mapping* — defined in code with the item, Tharstern its first
 * column and Solopress the second ("the Solopress mapping"). Supplier names
 * appear because these are internal admin pages. Cost, not price: every
 * figure here is what we would pay.
 */

import type { components } from "./api-schema.generated";
import type { ValidationMatrixRow } from "./cost-engine-validation-types";

type Schemas = components["schemas"];

// ── Routes ──────────────────────────────────────────────────────────────────

/** A fulfilment route as the admin pages name it. `code` is stable
 *  (`in_house`, `solopress`, later `tradeprint`, one per PDQ supplier…);
 *  `label` is what the page shows. */
export type FulfilmentRouteRef = Schemas["FulfilmentRouteRef"];

/** The in-house route's code — the one route every made-in-house item has. */
export const IN_HOUSE_ROUTE_CODE = "in_house";

/** Freshness of one supplier cost, derived at read time, never stored
 *  (brief §6; #35; TTL 36 h, Ben 24 Sep): `live` refreshed by last night's
 *  run; `expiring` one missed night — the previous figure is still inside
 *  36 hours; `expired` past 36 hours — never shown as a cost;
 *  `not_yet_quoted` mapped, but no nightly run has quoted it yet;
 *  `unmapped` the item's mapping has no leaf (or no equivalent) for this
 *  configuration; `errored` a leaf it uses failed last night's run, so the
 *  route is unavailable here until a PR fixes it. */
export type SupplierCostFreshness =
  Schemas["SupplierRouteCostCell"]["freshness"];

// ── Journey B: the dictionary index with route coverage ─────────────────────

/** One route's availability for one dictionary item (#24, #36). */
export type RouteAvailability = Schemas["RouteAvailability"];

/** "How many routes could make this item?" — the #20 coverage goal is
 *  `target` (three). */
export type RouteCoverage = Schemas["RouteCoverage"];

/** The route-coverage sort key the index list accepts beside its existing
 *  ones (`sort=available_route_count` / `-available_route_count`), applied
 *  server-side like every other sort (docs/list-endpoints.md). */
export const AVAILABLE_ROUTE_COUNT_SORT = "available_route_count";

// ── Journey C: the overnight banner ─────────────────────────────────────────
// One chained nightly run (#48): each supplier's product-list refresh, then
// its quotes, then the in-house catch-up — the `warm` half of the dictionary
// index's populate-status endpoint.

export type FulfilmentRoutePopulateStatus =
  Schemas["FulfilmentRoutePopulateStatusSchema"];
export type SupplierRouteUpkeep = Schemas["SupplierRouteUpkeepSchema"];
export type InHouseCatchUpRun = Schemas["InHouseCatchUpRunSchema"];
/** One mapping leaf that failed last night's run — the signal that a PR is
 *  needed. `path` walks the item's tree to it in our own labels. */
export type FailedMappingLeaf = Schemas["FailedMappingLeafSchema"];

// ── Journey A: the cost matrix with a route dimension ───────────────────────

/** One supplier cost in the matrix. `netPence` is null unless the cost is
 *  live or expiring — an expired cost is never shown (brief §6). */
export type SupplierRouteCostCell = Schemas["SupplierRouteCostCell"];

/** One supplier route's row beneath a variant. */
export type SupplierRouteRow = Schemas["SupplierRouteRow"];

/** One in-house cell: ONE figure and what it is (Ben, 24 Sep) — checked (the
 *  checked figure and its verdict), not yet checked (the engine's cost), the
 *  cached Tharstern estimate while live costing is paused, or — for a
 *  supplier-only item (#71) — no in-house route at all. Classified once,
 *  server-side, for the grid and the cost-pair page alike. */
export type InHouseCell = ValidationMatrixRow["inHouse"][number];

/** This route's mapping, summarised beside it above the matrix. */
export type MappingSummary = Schemas["MappingSummary"];

/** The in-house route's checks for this item, by verdict. */
export type InHouseCheckCounts = Schemas["InHouseCheckCounts"];

/** The routes summary block above the matrix: an in-house entry (if the
 *  item is made in-house, #71) plus one per known supplier route. */
export type MatrixRouteSummary = Schemas["MatrixRouteSummary"];

// ── Journey A drill-down: one supplier cost ─────────────────────────────────

export type SupplierRouteCostPair = Schemas["SupplierRouteCostPair"];
/** One attribute of the request we send the supplier, with where it came
 *  from on our side (`sourceKind` plus, for an option, its field and option
 *  labels). */
export type SupplierRequestAttribute = Schemas["RouteRequestAttribute"];
export type SupplierFetchAttempt = Schemas["RouteCostAttempt"];

// ── Journey D: an item's mappings, as defined in code ───────────────────────
// Ben, 24 Sep: a dictionary item is code — one file per item, every
// back-end's mapping nested inline. There is no mapping lifecycle: a failing
// leaf is a bug fixed by PR, and until then that route is unavailable for
// the configurations it touches. Errors are the only signal.

export const THARSTERN_BACK_END_CODE = "tharstern";

/** A back-end an item maps into, in column order (Tharstern first). */
export type MappingBackEnd = Schemas["FulfilmentRouteRefSchema"];
export type MappingLeafError = Schemas["MappingLeafErrorSchema"];
export type ItemBackEndMapping = Schemas["ItemBackEndMappingSchema"];
/** A dictionary item's mapping tree: the item row per back-end, then each
 *  field's slot, then each option's value. A back-end missing from
 *  `item.byBackEnd` has no subtree for this item at all. */
export type ItemMappings = Schemas["DictionaryItemMappingsSchema"];
